import { useState, useEffect } from "react";
import { Button } from "@shared/components/ui/button";
import { Input } from "@shared/components/ui/input";
import { Label } from "@shared/components/ui/label";
import { useUpdateSetting } from "./useSettings";
import { toast } from "sonner";
import { Textarea } from "@shared/components/ui/textarea";
import { useTranslation } from "react-i18next";
import { Save, RefreshCw } from "lucide-react";

const SettingsForm = ({ settingsData }) => {
  const { t } = useTranslation();
  const { mutate: updateSettings, isPending } = useUpdateSetting();
  const [formData, setFormData] = useState({
    siteName: "",
    siteDescription: "",
    contactEmail: "",
  });
  const [errors, setErrors] = useState({});

  // Update form data when settingsData changes
  useEffect(() => {
    if (settingsData) {
      setFormData({
        siteName: settingsData.siteName || "",
        siteDescription: settingsData.siteDescription || "",
        contactEmail: settingsData.contactEmail || "",
      });
    }
  }, [settingsData]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.siteName.trim()) {
      newErrors.siteName = t("validation.required", {
        field: t("settings.siteName"),
      });
    }

    if (!formData.contactEmail.trim()) {
      newErrors.contactEmail = t("validation.required", {
        field: t("settings.contactEmail"),
      });
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.contactEmail)) {
      newErrors.contactEmail = t("validation.invalidEmail", {
        default: "Please enter a valid email address",
      });
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    updateSettings(formData, {
      onSuccess: () => {
        toast.success(t("messages.settingsUpdated"));
      },
      onError: (error) => {
        toast.error(
          t("messages.updateError", {
            default: `Error updating settings: ${error.message}`,
          })
        );
      },
    });
  };

  const handleReset = () => {
    if (settingsData) {
      setFormData({
        siteName: settingsData.siteName || "",
        siteDescription: settingsData.siteDescription || "",
        contactEmail: settingsData.contactEmail || "",
      });
    }
    setErrors({});
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid gap-6 md:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="siteName">
            {t("settings.siteName")} <span className="text-red-500">*</span>
          </Label>
          <Input
            id="siteName"
            name="siteName"
            value={formData.siteName}
            onChange={handleInputChange}
            placeholder={t("settings.siteNamePlaceholder", {
              default: "Enter site name",
            })}
            className={errors.siteName ? "border-red-500" : ""}
          />
          {errors.siteName && (
            <p className="text-sm text-red-500">{errors.siteName}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="contactEmail">
            {t("settings.contactEmail")} <span className="text-red-500">*</span>
          </Label>
          <Input
            id="contactEmail"
            name="contactEmail"
            type="email"
            value={formData.contactEmail}
            onChange={handleInputChange}
            placeholder={t("settings.contactEmailPlaceholder", {
              default: "contact@example.com",
            })}
            className={errors.contactEmail ? "border-red-500" : ""}
          />
          {errors.contactEmail && (
            <p className="text-sm text-red-500">{errors.contactEmail}</p>
          )}
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="siteDescription">{t("settings.siteDescription")}</Label>
        <Textarea
          id="siteDescription"
          name="siteDescription"
          value={formData.siteDescription}
          onChange={handleInputChange}
          placeholder={t("settings.siteDescriptionPlaceholder", {
            default: "Describe your delivery service...",
          })}
          rows={4}
        />
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <Button
          type="submit"
          disabled={isPending}
          className="flex items-center gap-2"
        >
          {isPending ? (
            <RefreshCw className="h-4 w-4 animate-spin" />
          ) : (
            <Save className="h-4 w-4" />
          )}
          {isPending ? t("settings.updating") : t("settings.updateSettings")}
        </Button>

        <Button
          type="button"
          variant="outline"
          onClick={handleReset}
          disabled={isPending}
        >
          {t("settings.reset", { default: "Reset" })}
        </Button>
      </div>
    </form>
  );
};

export default SettingsForm;
